import type {
  RecordSkillOutput,
  RecordSkillOutputSchema,
} from '@/types/record-skill';

export function outputKey(label: string): string {
  return label
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^[^a-z]+|_+$/g, '')
    .slice(0, 40);
}

/** Empty fields intentionally restore the legacy free-form output. */
export function outputSchema(
  fields: RecordSkillOutput[]
): RecordSkillOutputSchema | null {
  return fields.length ? { version: 1, fields } : null;
}

export function outputFieldsError(
  fields: RecordSkillOutput[]
): string | undefined {
  if (fields.length > 12) return 'Use up to 12 outputs';
  const keys = new Set<string>();
  const labels = new Set<string>();
  for (const field of fields) {
    if (!field.label.trim() || field.label.length > 60)
      return 'Add a name of up to 60 characters';
    if (labels.has(field.label.toLocaleLowerCase()))
      return 'Names must be unique';
    labels.add(field.label.toLocaleLowerCase());
    if ((field.description?.length ?? 0) > 500)
      return 'Descriptions allow up to 500 characters';
    if (!/^[a-z][a-z0-9_]{0,39}$/.test(field.key))
      return 'Keys start with a letter and use up to 40 lowercase letters, numbers or underscores';
    if (
      ['markdown', 'structured', 'structured_failed', 'trace'].includes(
        field.key
      )
    )
      return `${field.key} is a reserved key`;
    if (keys.has(field.key)) return 'Keys must be unique';
    keys.add(field.key);
    if (field.type === 'enum') {
      const options = field.options ?? [];
      if (options.length < 2) return 'Add at least two choices';
      if (options.length > 12) return 'Use up to 12 choices';
      if (new Set(options).size !== options.length)
        return 'Choices must be unique';
      if (options.some((option) => !option.trim() || option.length > 60))
        return 'Choices must have 1 to 60 characters';
    }
    if (field.type === 'number') {
      if (
        [field.min, field.max].some(
          (n) => n !== undefined && !Number.isFinite(n)
        )
      )
        return 'Use a valid number';
      if (
        field.min !== undefined &&
        field.max !== undefined &&
        field.min > field.max
      )
        return 'Minimum must be less than or equal to maximum';
    }
    if (
      field.type === 'text' &&
      field.maxLength !== undefined &&
      (!Number.isInteger(field.maxLength) ||
        field.maxLength < 1 ||
        field.maxLength > 2000)
    )
      return 'Text limit must be 1 to 2,000 characters';
    if (
      field.type === 'list' &&
      field.maxItems !== undefined &&
      (!Number.isInteger(field.maxItems) ||
        field.maxItems < 1 ||
        field.maxItems > 20)
    )
      return 'List limit must be 1 to 20 items';
  }
}
